import React, { useEffect } from 'react';

import { Box, CircularProgress, Container } from '@material-ui/core';
import { useBackRouter } from 'hooks';
import { GetStaticPaths, GetStaticPropsContext, InferGetStaticPropsType, NextPage } from 'next';
import { NextSeo } from 'next-seo';
import { QueryKeys, useGetGameDetails, usePlayGame } from 'queries';
import { useIntl } from 'react-intl';
import { dehydrate, QueryClient } from 'react-query';
import { Routes } from 'routes';

import { generatePath } from 'utils/routes.utils';
import { enhancePathsWithLocales } from 'utils/routes.utils';

import { Duration } from 'constants/cache';
import { Games } from 'constants/games';
import apiService from 'services/api/ApiService';
import { ContentType, GameType } from 'services/api/definitions/game.definitions';

import NavigationLayout from 'components/@layout/NavigationLayout/NavigationLayout';

const Game: NextPage<InferGetStaticPropsType<typeof getStaticProps>> = ({ id = '' }) => {
  const intl = useIntl();
  const { game } = useGetGameDetails(id);
  const { startGame } = usePlayGame();

  useEffect(() => {
    if (game && game.id && game.name) {
      startGame({ id: game.id, name: game.name });
    }
  }, [game, startGame]);

  const router = useBackRouter(
    game ? generatePath(Routes.gameDetails, { slug: game.slugName }) : undefined,
  );

  if (router.isFallback || !game || game.contentType === ContentType.subscription) {
    return (
      <Box height="100%" display="flex" alignItems="center" justifyContent="center">
        <CircularProgress size={24} />
      </Box>
    );
  }

  const gameToRender = Games(game);
  const type = game.gameType;

  return (
    <NavigationLayout
      gameType={game.gameType}
      behaviour="fixed"
      hideBottomNavigation
      backAction={router.back}
    >
      <NextSeo
        title={intl.formatMessage({ id: 'seo.title.game.slug' }, { game: game.name })}
        description={game.description}
        openGraph={{ images: [{ url: game.largeTeaserImage }] }}
      />
      <main style={{ height: '100%' }}>
        {type === GameType.thirdParty && (
          <Container maxWidth="sm" disableGutters style={{ height: '100%' }}>
            <Box
              height="100%"
              display="flex"
              justifyContent="center"
              alignItems="center"
              position="relative"
            >
              {gameToRender}
            </Box>
          </Container>
        )}
        {type === GameType.firstParty && gameToRender}
      </main>
    </NavigationLayout>
  );
};

export const getStaticPaths: GetStaticPaths = async ({ locales }) => {
  const games = await apiService.getGameSlugs();
  const paths = games.map(game => ({ params: { slug: game.slugName } }));

  return { paths: enhancePathsWithLocales(paths, locales), fallback: true };
};

export const getStaticProps = async ({ params }: GetStaticPropsContext<{ slug: string }>) => {
  const queryClient = new QueryClient();

  const games = await apiService.getGameSlugs();
  const id = games.find(g => g.slugName === params?.slug)?.id;

  if (!id) {
    return {
      notFound: true,
      revalidate: Duration.ONE_HOUR,
    };
  }

  await queryClient.prefetchQuery(QueryKeys.gamedetail.byId(id), () =>
    apiService.getGameDetails(id),
  );

  return {
    props: {
      dehydratedState: dehydrate(queryClient),
      id,
    },
    revalidate: Duration.ONE_HOUR,
  };
};

export default Game;
